/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { lazy, Suspense, useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { jsx } from '@atlaskit/css';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Inline } from '@atlaskit/primitives/compiled';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';
import { useRovoUserSettings } from '@atlassian/conversation-assistant-store/controllers/rovo-user-settings';
import { useSideNavModal } from '@atlassian/conversation-assistant-ui-components/ui/side-nav-modal/side-nav-modal-provider';
import type {
	SideNavModalItem,
	SubScreenActions,
} from '@atlassian/conversation-assistant-ui-components/ui/side-nav-modal/side-nav-modal-provider/modal';
import { ManageMemoryContent } from '@atlassian/rovo-memories/ui/manage-memory-content';
import { ManageMemoryEditsSubscreen } from '@atlassian/rovo-memories/ui/manage-memory-edits-subscreen';
import { MemoryModalSubscreen } from '@atlassian/rovo-memories/ui/memory-modal-subscreen';
import { WorkProfileScreen } from '@atlassian/rovo-memories/ui/work-activity-subscreen';

import { useRovoSettingsConfig } from '../../../../common/utils/rovo-settings-config';
import { useAssistanceServiceParams } from '../../../../common/utils/use-assistance-service-params';
import { isAtlassianEmployee } from '../../../../common/utils/user-utils';

import { messages } from './messages';
import {
	importResponseStyleTab,
	importResponseWritingStyleSubScreen,
	prefetchResponseWritingStyleChunks,
} from './prefetch-response-writing-style';
import { useMemories } from './use-memories';
import { useMemoryBlurb } from './use-memory-blurb';
import { useCreateMemory } from './useCreateMemory';
import { useCreateMemoryEdit } from './useCreateMemoryEdit';
import { useMemoryEdits } from './useMemoryEdits';
import { useMemoryEditsCount } from './useMemoryEditsCount';

// Lazy-loaded so the response-and-writing-styles bundle is only fetched when the
// gate is enabled and the user opens the subscreen.
const LazyResponseWritingStyleSubScreen = lazy(() =>
	importResponseWritingStyleSubScreen().then((module) => ({
		default: module.ResponseWritingStyleSubScreen,
	})),
);

const LazyResponseStyleTab = lazy(() =>
	importResponseStyleTab().then((module) => ({ default: module.ResponseStyleTab })),
);

const ResponseWritingStyleSubScreenContent = (): React.JSX.Element => {
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'rovo-settings-modal-side-nav-response-style',
	});

	return (
		<Suspense fallback={null}>
			<LazyResponseWritingStyleSubScreen
				responseStyleContent={
					<LazyResponseStyleTab assistanceServiceParams={assistanceServiceParams} />
				}
			/>
		</Suspense>
	);
};
ResponseWritingStyleSubScreenContent.displayName = 'lazy(ResponseWritingStyleSubScreen)';

/**
 * A self-contained subscreen component that owns its own data-fetching hooks.
 *
 * This is intentionally defined at module scope (outside MemoryContentContainer)
 * so that React treats it as a stable component type across renders. When
 * pushSubscreen stores `<MemorySubscreenContent />` as a React node, React will
 * continue to mount and re-render this component as normal — meaning it always
 * reflects the latest data from useMemories and useMemoryBlurb rather than a
 * stale snapshot captured at the moment pushSubscreen was called.
 */
type MemorySubscreenContentProps = {
	feedbackUrl?: string;
	isInternalUser?: boolean;
	onShareFactWithRovo?: () => void;
	onManageMemoryEdits: () => void;
};

const MemorySubscreenContent = ({
	feedbackUrl,
	isInternalUser,
	onShareFactWithRovo,
	onManageMemoryEdits,
}: MemorySubscreenContentProps): React.JSX.Element => {
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'rovo-settings-modal-side-nav-memory',
	});

	const {
		memories,
		isLoading: isMemoriesLoading,
		onRetry: refetchMemories,
		onDeleteMemory,
		error: memoriesError,
	} = useMemories({
		assistanceServiceParams,
		isEnabled: true,
	});

	const {
		blurb: memoryBlurb,
		isLoading: isMemoryBlurbLoading,
		onRetry: refetchBlurb,
		error: memoryBlurbError,
	} = useMemoryBlurb({
		assistanceServiceParams,
		isEnabled: true,
	});

	const { count: memoryEditsCount, refetch: refetchMemoryEditsCount } = useMemoryEditsCount({
		assistanceServiceParams,
		isEnabled: true,
	});

	const {
		onCreateMemoryEdit,
		isSubmitting: isSubmittingMemoryEdit,
		hasError: hasMemoryEditError,
	} = useCreateMemoryEdit({
		assistanceServiceParams,
		onSuccess: () => refetchMemoryEditsCount(),
	});

	const { onCreateMemory, isSubmitting: isCreatingMemory } = useCreateMemory({
		assistanceServiceParams,
		onSuccess: () => refetchMemories(),
	});

	return (
		<MemoryModalSubscreen
			onDeleteExplicitMemory={onDeleteMemory}
			explicitMemories={memories}
			isExplicitMemoryError={!!memoriesError}
			onExplicitMemoryRetry={refetchMemories}
			isExplicitMemoryLoading={isMemoriesLoading}
			profileMemory={memoryBlurb}
			isProfileMemoryLoading={isMemoryBlurbLoading}
			isProfileMemoryError={!!memoryBlurbError}
			onExplicitMemoryTabSelected={refetchMemories}
			onProfileMemoryTabSelected={refetchBlurb}
			feedbackUrl={feedbackUrl}
			isInternalUser={isInternalUser}
			onShareFactWithRovo={onShareFactWithRovo}
			memoryEditsCount={memoryEditsCount}
			onManageMemoryEdits={onManageMemoryEdits}
			onCreateMemoryEdit={onCreateMemoryEdit}
			isSubmittingMemoryEdit={isSubmittingMemoryEdit}
			hasMemoryEditError={hasMemoryEditError}
			onAddMemory={onCreateMemory}
			isCreatingMemory={isCreatingMemory}
		/>
	);
};

/**
 * A self-contained subscreen component for managing memory edits.
 * Owns its own data-fetching hooks so it always reflects live data.
 */
const MemoryEditsSubscreenContent = (): React.JSX.Element => {
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'rovo-settings-modal-side-nav-memory',
	});

	// Refetch count so the badge updates when the user navigates back.
	const { refetch: refetchMemoryEditsCount } = useMemoryEditsCount({
		assistanceServiceParams,
		isEnabled: true,
	});

	const { edits, isLoading, onRetry, onDeleteEdit, error } = useMemoryEdits({
		assistanceServiceParams,
		isEnabled: true,
		onDeleteSuccess: refetchMemoryEditsCount,
	});

	return (
		<ManageMemoryEditsSubscreen
			edits={edits}
			isLoading={isLoading}
			isError={!!error}
			onRetry={onRetry}
			onDeleteEdit={onDeleteEdit}
		/>
	);
};

const MEMORY_FEEDBACK_URL = 'https://go/memory_feedbacks';

type MemoryContentContainerProps = {
	pushSubscreen: SubScreenActions['pushSubscreen'];
};

/**
 * Stable container that encapsulates memory management data fetching and wires
 * {@link ManageMemoryContent} for the side nav modal. Keeps a stable component
 * reference so the parent item is not recreated when memory data or settings change.
 *
 * Receives {@link SubScreenActions['pushSubscreen']} as a prop to push a full-screen
 * subscreen when the user wants to view all memories beyond the default page limit.
 */
export const MemoryContentContainer = ({
	pushSubscreen,
}: MemoryContentContainerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { onSeeWhatRovoKnows } = useRovoSettingsConfig();
	const { close: closeModal } = useSideNavModal();
	const { sendAnalyticsEvent } = useAnalytics();

	const handleSeeWhatRovoKnows = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'seeWhatRovoKnows',
			source: 'memoryManagementSettings',
		});
		onSeeWhatRovoKnows?.();
		closeModal();
	}, [onSeeWhatRovoKnows, closeModal, sendAnalyticsEvent]);

	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'rovo-settings-modal-side-nav-memory',
	});

	const {
		settings,
		updateSettings,
		isFetching: isFetchingSettings,
	} = useRovoUserSettings({
		assistanceServiceParams,
		enabled: true,
	});

	const profileData = useProfileController();
	const isInternalUser = isAtlassianEmployee(profileData?.email);

	const handleAllowConversationReferenceChange = useCallback(
		async (enabled: boolean): Promise<void> => {
			await updateSettings({
				allowConversationReference: enabled,
			});
		},
		[updateSettings],
	);

	const handleAllowMemoryChange = useCallback(
		async (enabled: boolean): Promise<void> => {
			await updateSettings({
				allowMemory: enabled,
			});
		},
		[updateSettings],
	);

	const handleWebSearchDefaultEnabledChange = useCallback(
		async (enabled: boolean): Promise<void> => {
			await updateSettings({
				webSearchDefaultEnabled: enabled,
			});
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'change',
				actionSubject: 'toggle',
				actionSubjectId: 'rovoChatWebSearchDefaultPreference',
				source: 'memoryManagementSettings',
				attributes: {
					enabled,
				},
			});
		},
		[updateSettings, sendAnalyticsEvent],
	);

	const handleShareFactWithRovo = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'shareFactWithRovo',
			source: 'memoryModalSubscreen',
		});
		closeModal();
	}, [closeModal, sendAnalyticsEvent]);

	const handleManageMemoryEdits = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'manageMemoryEdits',
			source: 'memoryModalSubscreen',
		});
		pushSubscreen({
			backLabel: formatMessage(messages.backToRovoMemory),
			content: <MemoryEditsSubscreenContent />,
		});
	}, [pushSubscreen, formatMessage, sendAnalyticsEvent]);

	const onManageMemories = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'manageMemories',
			source: 'memoryManagementSettings',
		});
		pushSubscreen({
			backLabel: formatMessage(messages.backToRovoMemory),
			// MemorySubscreenContent owns its own data-fetching hooks so the
			// subscreen always reflects live data instead of a stale snapshot
			// captured at the moment pushSubscreen was called.
			content: (
				<MemorySubscreenContent
					feedbackUrl={MEMORY_FEEDBACK_URL}
					isInternalUser={isInternalUser}
					onShareFactWithRovo={handleShareFactWithRovo}
					onManageMemoryEdits={handleManageMemoryEdits}
				/>
			),
		});
	}, [
		pushSubscreen,
		formatMessage,
		isInternalUser,
		handleShareFactWithRovo,
		handleManageMemoryEdits,
		sendAnalyticsEvent,
	]);

	const onSeeMyActivity = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'seeMyActivity',
			source: 'memoryManagementSettings',
		});
		pushSubscreen({
			backLabel: formatMessage(messages.backToRovoSettings),
			content: (
				<WorkProfileScreen
					seeAllActivityHref={`${window.location.origin}/people/${profileData?.aaid}/work`}
				/>
			),
		});
	}, [pushSubscreen, formatMessage, profileData?.aaid, sendAnalyticsEvent]);

	const onEditResponseWritingStyle = useCallback(() => {
		// Start both chunk downloads in parallel as soon as the user opts in, so
		// they overlap with pushing the subscreen rather than loading serially.
		prefetchResponseWritingStyleChunks();
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'editResponseWritingStyle',
			source: 'memoryManagementSettings',
		});
		pushSubscreen({
			backLabel: formatMessage(messages.backToRovoPersonalization),
			content: <ResponseWritingStyleSubScreenContent />,
		});
	}, [pushSubscreen, formatMessage, sendAnalyticsEvent]);

	const isWebSearchPreferenceEnabled = fg('rovo_chat_web_search_preference');

	return (
		<ManageMemoryContent
			allowMemory={settings?.allowMemory}
			allowConversationReference={settings?.allowConversationReference}
			onAllowConversationReferenceChange={handleAllowConversationReferenceChange}
			onAllowMemoryChange={handleAllowMemoryChange}
			webSearchDefaultEnabled={settings?.webSearchDefaultEnabled}
			onWebSearchDefaultEnabledChange={
				isWebSearchPreferenceEnabled ? handleWebSearchDefaultEnabledChange : undefined
			}
			isFetchingSettings={isFetchingSettings}
			onManageMemories={onManageMemories}
			onSeeWhatRovoKnows={onSeeWhatRovoKnows ? handleSeeWhatRovoKnows : undefined}
			onSeeMyActivity={onSeeMyActivity}
			onEditResponseWritingStyle={onEditResponseWritingStyle}
		/>
	);
};

/**
 * Side nav modal item for Manage memory. Memoised so the object reference only
 * changes when locale/messages change; {@link MemoryContentContainer} stays stable.
 */
export const useManageMemorySideNavModalItem = (): SideNavModalItem => {
	const { formatMessage } = useIntl();

	return useMemo(() => {
		return {
			id: 'manage-memory',
			label: (
				<Inline space="space.100" alignBlock="center" spread="space-between" grow="fill">
					{formatMessage(messages.viewMoreManageMemory)}
				</Inline>
			),
			icon: <CustomizeIcon label="" />,
			contentHeader: formatMessage(messages.manageMemoryModalMainHeader),
			content: ({ pushSubscreen }: SubScreenActions) => (
				<MemoryContentContainer pushSubscreen={pushSubscreen} />
			),
		};
	}, [formatMessage]);
};
